import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, IconButton, Menu, MenuItem } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import * as patientActions from "../../core/actions/patientActions";
import { patientServices } from "../../core/services";
import {
  ConsentFormStatus,
  FormGroupLable,
  FormGroupName,
} from "../../shared/constants";
import { helperServices } from "../../shared/helper";
import PrintAdmissionConsent from "./print/PrintAdmissionConsent";
import PrintPainAssessment from "./print/PrintPainAssessment";
import PrintPanchkarmaConsent from "./print/PrintPanchkarmaConsent";
import PrintSurgeryConsent from "./print/PrintSurgeryConsent";

const parseJsonArray = (value) => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const ConsentFormActions = ({
  completing,
  form,
  onComplete,
  onEdit,
  onPrint,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isCompleted = form.status === ConsentFormStatus.completed;

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        gap: 4,
        height: "100%",
        justifyContent: "center",
      }}
    >
      {!isCompleted && (
        <IconButton
          aria-label="View or edit consent form"
          color="primary"
          size="small"
          title="View/Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(form);
          }}
        >
          <span className="material-icons" style={{ fontSize: 18 }}>
            edit
          </span>
        </IconButton>
      )}
      <IconButton
        aria-label="Print consent form"
        color="primary"
        size="small"
        title="Print"
        onClick={(event) => {
          event.stopPropagation();
          onPrint(form);
        }}
      >
        <span className="material-icons" style={{ fontSize: 18 }}>
          print
        </span>
      </IconButton>
      {!isCompleted && (
        <>
          <IconButton
            aria-label="More consent form actions"
            disabled={completing}
            size="small"
            title="More actions"
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <span className="material-icons" style={{ fontSize: 19 }}>
              more_vert
            </span>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              disabled={completing}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(null);
                onComplete(form);
              }}
            >
              <span className="material-icons mr-2" style={{ fontSize: 18 }}>
                check_circle
              </span>
              Complete
            </MenuItem>
          </Menu>
        </>
      )}
    </div>
  );
};

const ConsentFormsDashboard = (props) => {
  const dispatch = useDispatch();  

  const { patientId, visitId } = props.match.params;

  const [anchorEl, setAnchorEl] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [ipopNumber, setIpopNumber] = useState("");

  const [consentForms, setConsentForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [printJob, setPrintJob] = useState(null);
  const [completingForm, setCompletingForm] = useState("");

  useEffect(() => {
    if (!patientId || !visitId) {
      return;
    }

    dispatch(
      patientActions.getPatientProfile(
        patientId,
        visitId,
        (resCb) => {
          if (!resCb) {
            return;
          }

          const fullName = `${resCb.fname || ""} ${
            resCb.mname || ""
          } ${resCb.lname || ""}`
            .replace(/\s+/g, " ")
            .trim();

          setPatientName(fullName);
          setIpopNumber(resCb.visit?.ipopnumber || resCb.ipopnumber || "");
        }
      )
    );
  }, [dispatch, patientId, visitId]);

  useEffect(() => {
    if (!patientId || !visitId) {
      return;
    }

    setLoadingForms(true);

    const consentGroups = [
      FormGroupName.Surgery_Consent_Form,
      FormGroupName.Admission_Consent_Form,
      FormGroupName.Panchakarma_Consent_Form,
      FormGroupName.Pain_Assessment_Form,
    ];

    Promise.all(
      consentGroups.map((groupname) =>
        patientServices
          .getDischargeSummary(
            {},
            {
              patientId,
              visitId,
              groupname,
            }
          )
          .then((res) => ({ groupname, response: res }))
          .catch(() => ({ groupname, response: null }))
      )
    )
      .then((results) => {

        const groupedData = results
          .filter(
            ({ response }) =>
              Array.isArray(response?.procedureList) &&
              response.procedureList.length > 0
          )
          .map(({ groupname, response }) => {

            const uniqueSubgroups = Object.values(
              response.procedureList.reduce((acc, row) => {
                if (!row.subgroup) return acc;

                if (!acc[row.subgroup]) {
                  acc[row.subgroup] = {
                    subgroup: row.subgroup,
                    values: {},
                    rowIds: {},
                    udt: row.udt,
                    groupname: row.groupname || groupname,
                    status: ConsentFormStatus.inprogress,
                  };
                }

                acc[row.subgroup].values[row.claSS] = row.notes;
                acc[row.subgroup].rowIds[row.claSS] = row.id;

                if (row.claSS === "status") {
                  acc[row.subgroup].status = row.notes;
                }

                if (
                  row.udt &&
                  (!acc[row.subgroup].udt ||
                    new Date(row.udt) > new Date(acc[row.subgroup].udt))
                ) {
                  acc[row.subgroup].udt = row.udt;
                }

                return acc;
              }, {})
            );

            console.log("uniqueSubgroups : ", uniqueSubgroups);
            // return [
            //   // groupname,
            //   // subgroups: uniqueSubgroups,
            //   ... uniqueSubgroups
            // ];
            return uniqueSubgroups;
          });

        console.log("subgroupedData : ", groupedData);
        setConsentForms(groupedData.flat());


      })
      .finally(() => {
        setLoadingForms(false);
      });
  }, [patientId, visitId]);


  const handleAddConsentClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  async function openNewConsentForm(formType) {
    handleClose();

    try {
      const formResp = await patientServices.getNewFormId(patientId, visitId, formType);
      const subgroup = formResp?.procedure?.subgroup;

      if (!subgroup) {
        throw new Error("The server did not return a subgroup.");
      }

      const routes = {
        [FormGroupName.Surgery_Consent_Form]: `/SurgeryConsentForm/${patientId}/${visitId}/${subgroup}?mode=new`,
        [FormGroupName.Panchakarma_Consent_Form]: `/PanchakarmaConsentForm/${patientId}/${visitId}/${subgroup}?mode=new`,
        [FormGroupName.Admission_Consent_Form]: `/AdmissionConsentForm/${patientId}/${visitId}/${subgroup}?mode=new`,
        [FormGroupName.Pain_Assessment_Form]: `/PainAssessmentForm/${patientId}/${visitId}/${subgroup}?mode=new`,
      };

      props.history.push(routes[formType]);
    } catch (error) {
      console.error("Failed to create a new consent form:", error);
      alert("Unable to open the consent form. Please try again.");
    }
  };

  const openSavedConsentForm = (formName, subgroup) => {
    const routes = {
      [FormGroupName.Surgery_Consent_Form] : `/SurgeryConsentForm/${patientId}/${visitId}${subgroup ? `/${subgroup}` : ''}`,
      [FormGroupName.Admission_Consent_Form] : `/AdmissionConsentForm/${patientId}/${visitId}${subgroup ? `/${subgroup}` : ''}`,
      [FormGroupName.Panchakarma_Consent_Form] : `/PanchakarmaConsentForm/${patientId}/${visitId}${subgroup ? `/${subgroup}` : ''}`,
      [FormGroupName.Pain_Assessment_Form] : `/PainAssessmentForm/${patientId}/${visitId}${subgroup ? `/${subgroup}` : ''}`,
    };

    const route = routes[formName];

    if (route) {
      props.history.push(route);
    }
  };

  const printSavedConsentForm = (form) => {
    const values = form.values || {};
    const commonPatientInfo = {
      fullName: values.PatientName || patientName,
      age: values.Age,
      gender: values.Gender,
      id: values.UHID,
      ipopnumber: values.IPNo || values.OpdIpdNo || ipopNumber,
      roomName: values.Ward,
      doctorName: values.TreatingDoctor || values.DoctorName,
      diagnosis: values.Diagnosis,
      patientId,
      visitId,
    };

    if (
      form.groupname === FormGroupName.Surgery_Consent_Form ||
      form.groupname === FormGroupName.Admission_Consent_Form
    ) {
      const witnesses = parseJsonArray(values.WitnessList).map((witness) => ({
        name: witness.name,
        relation: witness.relation,
        signature: witness.signature || witness.sigUrl,
        signedAt: witness.signedAt,
        signedDate: helperServices.formatDateOnly(witness.signedAt, true),
        signedTime: helperServices.formatTimeOnly(witness.signedAt),
      }));

      setPrintJob({
        Component:
          form.groupname === FormGroupName.Surgery_Consent_Form
            ? PrintSurgeryConsent
            : PrintAdmissionConsent,
        regDate: values.Date,
        patientInfo: {
          ...commonPatientInfo,
          doctorSignature: values.DoctorSignature,
          doctorSignedDate: helperServices.formatDateOnly(
            values.DoctorSignedAt,
            true
          ),
          doctorSignedTime: helperServices.formatTimeOnly(values.DoctorSignedAt),
          patientSignature: values.PatientSignature,
          patientSignedDate: helperServices.formatDateOnly(
            values.PatientSignedAt,
            true
          ),
          patientSignedTime: helperServices.formatTimeOnly(values.PatientSignedAt),
          guardianName: values.GuardianName,
          guardianRelation: values.GuardianRelation,
          guardianSignature: values.GuardianSignature,
          guardianSignedDate: helperServices.formatDateOnly(
            values.GuardianSignedAt,
            true
          ),
          guardianSignedTime: helperServices.formatTimeOnly(
            values.GuardianSignedAt
          ),
          witnesses,
        },
      });
      return;
    }

    if (form.groupname === FormGroupName.Panchakarma_Consent_Form) {
      const procedures = parseJsonArray(values.PanchakarmaProcedures).map(
        (procedure) => ({
          procedureName: procedure.procedureName,
          takenBy: procedure.takenBy,
          date: procedure.date,
          signature: procedure.signature || procedure.sigUrl,
        })
      );

      setPrintJob({
        Component: PrintPanchkarmaConsent,
        patientInfo: {
          ...commonPatientInfo,
          procedures,
        },
      });
      return;
    }

    if (form.groupname === FormGroupName.Pain_Assessment_Form) {
      setPrintJob({
        Component: PrintPainAssessment,
        regDate: values.ExamDateTime,
        patientInfo: {
          ...commonPatientInfo,
          painScore: values.PainScore,
          doctorSignature: values.DoctorSignature,
          doctorSignedAt: values.DoctorSignedAt,
        },
      });
    }
  };

  const completeConsentForm = async (form) => {
    const formLabel = FormGroupLable[form.groupname] || "consent form";
    const confirmed = window.confirm(
      `Mark this ${formLabel} as completed? It will become read-only.`
    );

    if (!confirmed) return;

    const formKey = `${form.groupname}:${form.subgroup}`;
    setCompletingForm(formKey);

    try {
      const response = await patientServices.addToProcedure({
        ...(form.rowIds?.status != null ? { id: form.rowIds.status } : {}),
        claSS: "status",
        notes: ConsentFormStatus.completed,
        patientId,
        visitId,
        groupname: form.groupname,
        subgroup: form.subgroup,
      });

      if (response?.errorMessages?.length) {
        throw new Error(response.errorMessages[0]);
      }

      const returnedId =
        response?.procedure?.id || response?.procedureList?.[0]?.id;

      setConsentForms((currentForms) =>
        currentForms.map((currentForm) =>
          currentForm.groupname === form.groupname &&
          currentForm.subgroup === form.subgroup
            ? {
                ...currentForm,
                status: ConsentFormStatus.completed,
                udt: new Date().toISOString(),
                values: {
                  ...currentForm.values,
                  status: ConsentFormStatus.completed,
                },
                rowIds: {
                  ...currentForm.rowIds,
                  ...(returnedId != null ? { status: returnedId } : {}),
                },
              }
            : currentForm
        )
      );
    } catch (error) {
      console.error("Failed to complete consent form:", error);
      alert("Unable to complete the consent form. Please try again.");
    } finally {
      setCompletingForm("");
    }
  };

  const goToPatientSections = () => {
    props.history.push(`/PatientSections/${patientId}/${visitId}`);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "IP/OP Number",
        valueGetter: () => ipopNumber || "Not recorded",
        filter: "agTextColumnFilter",
        minWidth: 150,
        flex: 0.8,
      },
      {
        headerName: "Form Name",
        field: "groupname",
        filter: "agTextColumnFilter",
        minWidth: 220,
        valueGetter: (params) => FormGroupLable[params.data.groupname],
        flex: 1.5,
      },
      {
        headerName: "Updated Date & Time",
        field: "udt",
        filter: "agTextColumnFilter",
        minWidth: 220,
        valueGetter: (params) => helperServices.formatDateNTime(params.data.udt),
        flex: 1.5,
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 120,
        flex: 0.6,
      },
      {
        headerName: "Action",
        minWidth: 130,
        flex: 0.6,
        cellRendererFramework: (params) => (
          <ConsentFormActions
            completing={
              completingForm ===
              `${params.data.groupname}:${params.data.subgroup}`
            }
            form={params.data}
            onComplete={completeConsentForm}
            onEdit={(form) =>
              openSavedConsentForm(form.groupname, form.subgroup)
            }
            onPrint={printSavedConsentForm}
          />
        ),
      },
    ],
    [completingForm, ipopNumber]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  return (
    <div className="container-fluid p-3">

      {/* =====================================================
          HEADER + BREADCRUMBS (no Add button here anymore)
      ====================================================== */}
      <div className="mb-3">

        <Breadcrumbs aria-label="breadcrumb">

          <Typography
            color="text.primary"
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={goToPatientSections}
          >
            {patientName ? patientName.toUpperCase() : "Loading..."}
          </Typography>

          <Typography color="text.primary">
            Consent Forms
          </Typography>

        </Breadcrumbs>

        <h4 className="mt-2 mb-0">
          Consent Form Listings
        </h4>

      </div>

      {/* =====================================================
          AG-GRID SUBMISSION HISTORY TABLE
      ====================================================== */}
      <div className="card p-3 mb-3">

        <div className="ag-theme-balham" style={{ width: "100%" }}>
          <AgGridReact
            rowData={consentForms}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            domLayout="autoHeight"
            pagination
            paginationPageSize={10}
            animateRows
            onRowClicked={(event) => {
              if (event.data.status !== ConsentFormStatus.completed) {
                openSavedConsentForm(
                  event.data.groupname,
                  event.data.subgroup
                );
              }
            }}
            overlayNoRowsTemplate="No consent forms have been submitted yet."
            loading={loadingForms} 
          />
        </div>

      </div>
      {/* =====================================================
          ADD CONSENT FORM BUTTON - now below the grid
      ====================================================== */}
      <div className="d-flex justify-content-end">

        <Button
          variant="contained"
          color="primary"
          sx={{ textTransform: "none" }}
          onClick={handleAddConsentClick}
        >
          Add Consent Form
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => openNewConsentForm(FormGroupName.Surgery_Consent_Form)}>
            Surgery Consent
          </MenuItem>
          <MenuItem onClick={() => openNewConsentForm(FormGroupName.Panchakarma_Consent_Form)}>
            Panchakarma Consent
          </MenuItem>
          <MenuItem onClick={() => openNewConsentForm(FormGroupName.Admission_Consent_Form)}>
            Admission Consent
          </MenuItem>
          <MenuItem onClick={() => openNewConsentForm(FormGroupName.Pain_Assessment_Form)}>
            Pain Assessment
          </MenuItem>
        </Menu>

      </div>

      {printJob &&
        React.createElement(printJob.Component, {
          patientInfo: printJob.patientInfo,
          regDate: printJob.regDate,
          parentCallBack: () => setPrintJob(null),
        })}

    </div>
  );
};

export default ConsentFormsDashboard;
